import {useEffect, useRef} from 'react';
import {
    AsyncValueState,
    asyncValueCreatePendingState,
    asyncValueCreateRejectedState,
    asyncValueCreateResolvedState
} from '@wix/devzai-utils-common';
import {useForceUpdate} from './use-force-update';
import {useRenderState} from './use-boxed-value/use-boxed-value';

export namespace useAsyncOperation {
    export interface Dispatcher<T> {

        <R extends T>(promise: Promise<R>) : typeof promise;
        <R extends T>(promise: Promise<R>, returnPromise: true) : typeof promise;
        <R extends T>(promise: Promise<R>, returnPromise: false) : void;
        <R extends T>(promise: Promise<R>, returnPromise?: boolean) : ((typeof promise) | undefined);
    }
}

export function useAsyncOperation<T> (
    options?: {
        onResolved?: (value: T) => void;
        onRejected?: (reason: any) => void;
    }
): [AsyncValueState<T> | undefined, useAsyncOperation.Dispatcher<T>]
{
    const forceUpdate = useForceUpdate();

    const asyncValueRef = useRef<AsyncValueState<T>>();
    const pendingPromiseRef = useRef<Promise<T>>();

    const renderState = useRenderState({
        onResolved: options?.onResolved,
        onRejected: options?.onRejected
    })

    const setValue = ((promise: Promise<T>, returnPromise = true) => {

        if (promise !== pendingPromiseRef.current)
        {
            pendingPromiseRef.current = promise;

            asyncValueRef.current = asyncValueCreatePendingState(promise);

            promise
                .then(value => {
                    if (pendingPromiseRef.current === promise) {
                        pendingPromiseRef.current = undefined;
                        asyncValueRef.current = asyncValueCreateResolvedState(value);
                        forceUpdate();

                        renderState.current.onResolved?.(value);
                    }
                })
                .catch(error => {
                    if (pendingPromiseRef.current === promise) {
                        pendingPromiseRef.current = undefined;
                        asyncValueRef.current = asyncValueCreateRejectedState(error);
                        forceUpdate();

                        renderState.current.onRejected?.(error);
                    }
                });

            forceUpdate();
        }

        if (returnPromise) {
            return promise;
        } else {
            return undefined;
        }
    }) as useAsyncOperation.Dispatcher<T>;

    useEffect(() => {
        return () => {
            pendingPromiseRef.current = undefined;
        };
    }, []);

    return [asyncValueRef.current as AsyncValueState<T>, setValue];
}
